import { ModelCollection } from '/@/modules/core/app/models/ModelCollection';
import { DocumentExtraction } from '@/modules/ai/models/DocumentExtraction';
import { ExtractedUserCollection } from '@/modules/ai/models/ExtractedUserCollection';
import { ExtractionType } from '@/modules/ai/constants/ExtractionType';

export class DocumentExtractionCollection extends ModelCollection<DocumentExtraction> {
  constructor(items: DocumentExtraction[] | null = null) {
    super(items, DocumentExtraction);
  }

  static mapModel(items: DocumentExtraction[] | null = null): DocumentExtractionCollection {
    return new this(items);
  }

  get hasWillSpecificBeneficiariesExtraction(): boolean {
    return !!this.items?.find((item: DocumentExtraction) => item.isWillSpecificBeneficiariesType);
  }

  get hasWillResidualBeneficiariesExtraction(): boolean {
    return !!this.items?.find((item: DocumentExtraction) => item.isWillResidualBeneficiariesType);
  }

  get hasWillExecutorsExtraction(): boolean {
    return !!this.items?.find((item: DocumentExtraction) => item.isWillExecutorsType);
  }

  get hasAllSecureWillAndCodicilExtractions(): boolean {
    return (
      this.hasWillExecutorsExtraction &&
      this.hasWillResidualBeneficiariesExtraction &&
      this.hasWillSpecificBeneficiariesExtraction
    );
  }

  get hasAllValidatedSecureWillAndCodicilExtractions(): boolean {
    const secureWillExtractions: DocumentExtraction[] | undefined = this.items?.filter(
      (item: DocumentExtraction) => item.isSecureWillAndCodicilType
    );

    return (
      secureWillExtractions?.length === ExtractionType.getWillExtractionTypes().length &&
      secureWillExtractions?.every((extraction: DocumentExtraction) => extraction.isValidated)
    );
  }

  get hasAtLeastOneSecureWillAndCodicilExtraction(): boolean {
    return (
      this.hasWillExecutorsExtraction ||
      this.hasWillResidualBeneficiariesExtraction ||
      this.hasWillSpecificBeneficiariesExtraction
    );
  }

  get extractedUsers(): ExtractedUserCollection {
    return ExtractedUserCollection.mapModel(
      this.items?.map((item: DocumentExtraction) => item.extractedUsers.get() ?? []).flat()
    );
  }

  get secureWillExtractedUsers(): ExtractedUserCollection {
    return ExtractedUserCollection.mapModel(
      this.items
        ?.map(function (item: DocumentExtraction) {
          if (!item.isSecureWillAndCodicilType) {
            return [];
          }

          return item.extractedUsers.get() ?? [];
        })
        .flat()
    );
  }

  getDocumentExtractionByType(type: ExtractionType): DocumentExtraction | undefined {
    return this.items?.find(
      (documentExtraction: DocumentExtraction) => documentExtraction.extractionType === type
    );
  }

  getLatestDocumentExtractionByType(type: ExtractionType): DocumentExtraction | undefined {
    return this.items
      ?.filter(
        (documentExtraction: DocumentExtraction) => documentExtraction.extractionType === type
      )
      .pop();
  }
}
